import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { 
  Calculator, Clock, History, Lock, RefreshCw, Star, TrendingDown, TrendingUp, 
  User, Unlock, AlertCircle, CheckCircle 
} from 'lucide-react';

const RatingBreakdownModal = ({ 
  isOpen, 
  onClose, 
  editData, 
  liveProgressData,
  inspection,
  onRecalculate 
}) => {
  // Calculate the current breakdown of Q&A category ratings
  const ratingBreakdown = useMemo(() => {
    const categories = liveProgressData?.categories || [];
    const categoryRatings = editData?.category_ratings || {};
    
    // Get valid Q&A category IDs (excluding General Information)
    const qaCategories = categories.filter(cat => {
      const catId = (cat.category_id || '').toLowerCase();
      return !catId.includes('general_information') && !catId.includes('general information');
    });
    
    const breakdown = [];
    let totalRating = 0;
    let count = 0;
    
    qaCategories.forEach(cat => {
      const catId = cat.category_id || '';
      const catName = cat.category_name || catId;
      
      // Try to find the rating for this category
      const normalizedKey = catId.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      
      // Check multiple variations of the key
      let rating = null;
      const keysToTry = [catId, normalizedKey, catName.toLowerCase().replace(/[^a-z0-9]+/g, '_')];
      
      for (const key of keysToTry) {
        if (categoryRatings[key] !== undefined) {
          rating = typeof categoryRatings[key] === 'object' 
            ? categoryRatings[key].rating 
            : categoryRatings[key];
          break;
        }
      }
      
      if (rating !== null && rating !== undefined && rating > 0) {
        totalRating += rating;
        count++;
      }
      
      breakdown.push({
        id: catId,
        name: catName,
        rating: rating,
        included: rating !== null && rating !== undefined && rating > 0
      });
    });
    
    const calculatedAverage = count > 0 ? Math.round((totalRating / count) * 10) / 10 : 0;
    
    return {
      categories: breakdown,
      totalRating,
      count,
      calculatedAverage,
      currentOverallRating: editData?.overall_rating || 0,
      isLocked: editData?.overall_rating_locked || false
    };
  }, [editData, liveProgressData]);

  // Get the rating history
  const ratingHistory = useMemo(() => {
    return (editData?.overall_rating_history || inspection?.overall_rating_history || [])
      .slice()
      .reverse(); // Most recent first
  }, [editData, inspection]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 8) return 'text-green-600 bg-green-50';
    if (rating >= 6) return 'text-yellow-600 bg-yellow-50';
    if (rating >= 4) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            Overall Rating Breakdown
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          {/* Current Rating Summary */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Current Overall Rating</p>
                <div className="flex items-center gap-2">
                  <span className={`text-3xl font-bold ${getRatingColor(ratingBreakdown.currentOverallRating).split(' ')[0]}`}>
                    {ratingBreakdown.currentOverallRating}
                  </span>
                  <span className="text-gray-400 text-xl">/ 10</span>
                  <Star className="h-6 w-6 text-yellow-500 fill-yellow-400" />
                  {ratingBreakdown.isLocked ? (
                    <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                      <Lock className="h-3 w-3" /> Manually Set
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      <Unlock className="h-3 w-3" /> Auto-calculated
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Calculated Average</p>
                <p className="text-2xl font-semibold text-blue-600">
                  {ratingBreakdown.calculatedAverage}
                </p>
                <p className="text-xs text-gray-500">
                  ({ratingBreakdown.totalRating} ÷ {ratingBreakdown.count} categories)
                </p>
              </div>
            </div>
            
            {/* Show recalculate button if values don't match */}
            {ratingBreakdown.currentOverallRating !== ratingBreakdown.calculatedAverage && (
              <div className="mt-3 flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Current rating ({ratingBreakdown.currentOverallRating}) differs from calculated ({ratingBreakdown.calculatedAverage})
                    {ratingBreakdown.isLocked ? ' because it was manually set.' : ' due to old calculation method.'}
                  </span>
                </div>
                {onRecalculate && (
                  <Button 
                    size="sm" 
                    onClick={() => {
                      onRecalculate(ratingBreakdown.calculatedAverage);
                      onClose();
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Apply {ratingBreakdown.calculatedAverage}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Category Ratings Breakdown */}
          <div className="mb-6">
            <h3 className="font-semibold text-sm text-gray-800 mb-3 flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              Q&A Category Ratings (Used in Calculation)
            </h3>
            <div className="space-y-2">
              {ratingBreakdown.categories.map((cat, idx) => (
                <div 
                  key={cat.id || idx} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    cat.included 
                      ? 'bg-white border-gray-200' 
                      : 'bg-gray-50 border-gray-100 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      cat.included ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-700">
                      {cat.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {cat.included ? (
                      <>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getRatingColor(cat.rating)}`}>
                          {cat.rating}
                        </span>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not rated</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-blue-800">Total (Included Categories)</span>
                <span className="font-bold text-blue-900">
                  {ratingBreakdown.totalRating} ÷ {ratingBreakdown.count} = {ratingBreakdown.calculatedAverage}
                </span>
              </div>
            </div>
          </div>

          {/* Rating Change History */}
          <div>
            <h3 className="font-semibold text-sm text-gray-800 mb-3 flex items-center gap-2">
              <History className="h-4 w-4 text-purple-500" />
              Rating Change History
            </h3>
            
            {ratingHistory.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No rating changes recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ratingHistory.map((entry, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      entry.type === 'manual_override' 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {entry.type === 'manual_override' ? (
                          <Lock className="h-4 w-4 text-amber-600" />
                        ) : (
                          <Calculator className="h-4 w-4 text-green-600" />
                        )}
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          entry.type === 'manual_override' 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {entry.type === 'manual_override' ? 'Manual Override' : 'Auto Recalculation'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-gray-400 line-through">{entry.old_value || 0}</span>
                        {(entry.new_value || 0) > (entry.old_value || 0) ? (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                        <span className="font-bold text-gray-800">{entry.new_value}</span>
                      </div>
                    </div>
                    
                    <div className="mt-2 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>{entry.changed_by || 'System'}</span>
                        <span className="mx-1">•</span>
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(entry.timestamp)}</span>
                      </div>
                      {entry.reason && (
                        <p className="mt-1 text-gray-500">{entry.reason}</p>
                      )}
                    </div>
                    
                    {/* Show which category triggered the change */}
                    {entry.triggered_by && entry.triggered_by.length > 0 && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <p className="text-xs font-medium text-blue-700 mb-1">Category Change(s) that triggered this:</p>
                        <div className="space-y-1">
                          {entry.triggered_by.map((change, changeIdx) => (
                            <div key={changeIdx} className="flex items-center gap-2 text-xs">
                              <span className="font-medium text-blue-800">{change.displayName}</span>
                              <span className="text-gray-400">:</span>
                              <span className="text-red-500 line-through">{change.oldRating}</span>
                              <span className="text-gray-400">→</span>
                              <span className="text-green-600 font-semibold">{change.newRating}</span>
                              <span className={`text-xs ${change.newRating > change.oldRating ? 'text-green-500' : 'text-red-500'}`}>
                                ({change.newRating > change.oldRating ? '+' : ''}{change.newRating - change.oldRating})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Show calculation details for auto-recalculations */}
                    {entry.calculation && (
                      <div className="mt-2 p-2 bg-white rounded border border-gray-200 text-xs">
                        <p className="font-medium text-gray-700 mb-1">Calculation Details:</p>
                        <div className="grid grid-cols-2 gap-1">
                          {entry.calculation.categories?.map((cat, catIdx) => (
                            <div key={catIdx} className="flex justify-between">
                              <span className="text-gray-500 truncate">{cat.category?.replace(/_/g, ' ')}</span>
                              <span className="font-medium">{cat.rating}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-1 pt-1 border-t flex justify-between font-medium">
                          <span>Sum ÷ Count</span>
                          <span>{entry.calculation.sum} ÷ {entry.calculation.count} = {entry.calculation.average}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default RatingBreakdownModal;
